<script setup lang="ts">
import type { ComponentSize } from '@/types/ui';
import { Icon } from '@iconify/vue';
/**
 * UiAvatar - Design System Avatar Component
 *
 * Displays user avatars with image, initials, or icon fallback.
 *
 * @example
 * <UiAvatar src="/path/to/image.jpg" alt="John Doe" />
 * <UiAvatar name="John Doe" />
 * <UiAvatar icon="ph:user" size="lg" />
 */
import { computed, ref } from 'vue';

export interface UiAvatarProps {
   /** Image source URL */
   src?: string;
   /** Alt text for image */
   alt?: string;
   /** Name to generate initials from */
   name?: string;
   /** Fallback icon */
   icon?: string;
   /** Size variant */
   size?: Extract<ComponentSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;
   /** Shape variant */
   shape?: 'circle' | 'square';
   /** Show online status indicator */
   status?: 'online' | 'offline' | 'away' | 'busy';
}

const props = withDefaults(defineProps<UiAvatarProps>(), {
   size: 'md',
   shape: 'circle',
   icon: 'ph:user',
});

const imageError = ref(false);

const initials = computed(() => {
   if (!props.name)
      return '';
   const parts = props.name.trim().split(/\s+/);
   if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
   }
   return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
});

const showImage = computed(() => props.src && !imageError.value);
const showInitials = computed(() => !showImage.value && props.name);
const showIcon = computed(() => !showImage.value && !props.name);

const sizeClasses = computed(() => {
   const sizes = {
      xs: 'size-6 text-xs',
      sm: 'size-8 text-sm',
      md: 'size-10 text-base',
      lg: 'size-12 text-lg',
      xl: 'size-16 text-xl',
   };
   return sizes[props.size];
});

const iconSizeClasses = computed(() => {
   const sizes = {
      xs: 'size-3',
      sm: 'size-4',
      md: 'size-5',
      lg: 'size-6',
      xl: 'size-8',
   };
   return sizes[props.size];
});

const statusClasses = computed(() => {
   if (!props.status)
      return '';
   const colors = {
      online: 'bg-green-500',
      offline: 'bg-neutral',
      away: 'bg-yellow-500',
      busy: 'bg-red-500',
   };
   return colors[props.status];
});

const statusSizeClasses = computed(() => {
   const sizes = {
      xs: 'size-1.5 border',
      sm: 'size-2 border',
      md: 'size-2.5 border-2',
      lg: 'size-3 border-2',
      xl: 'size-4 border-2',
   };
   return sizes[props.size];
});

function handleImageError() {
   imageError.value = true;
}
</script>

<template>
   <div class="relative inline-flex">
      <div
         class="inline-flex items-center justify-center bg-neutral-light text-neutral-dark font-medium overflow-hidden"
         :class="[
            sizeClasses,
            shape === 'circle' ? 'rounded-full' : 'rounded-lg',
         ]"
      >
         <!-- Image -->
         <img
            v-if="showImage"
            :src="src"
            :alt="alt || name || 'Avatar'"
            class="size-full object-cover"
            @error="handleImageError"
         />

         <!-- Initials -->
         <span v-else-if="showInitials">
            {{ initials }}
         </span>

         <!-- Icon fallback -->
         <Icon v-else-if="showIcon" :icon="icon" :class="iconSizeClasses" />
      </div>

      <!-- Status indicator -->
      <span
         v-if="status"
         class="absolute bottom-0 right-0 rounded-full border-white"
         :class="[statusClasses, statusSizeClasses]"
      />
   </div>
</template>
